import { useFetchValidationSchema, useResource } from 'app/utils/hooks';
import { TcReleaseExportValidations, TuneCoreRelease } from 'types/releases';
import { useMemo, useState } from 'react';
import { transformSchemaToYup } from 'app/utils/schemaToYup';
import * as yup from 'yup';
import { fieldValidationMessages } from 'app/constants';
import { Link } from 'types/api.types';
import { fetchLanguageCodesDDLEffect } from 'app/store/SongList/effects';
import { getLanguageCodesSelector } from 'app/store/SongList/selectors';
import { useGenres } from 'app/views/Release/hooks';
import { getOptions } from 'app/components/Song/Details/SongDetailsForm/utils';
import { DDL, Option } from 'types/general.types';
import { fetchReleaseVersionEffect } from 'app/store/Releases/effects';
import { useAppDispatch } from 'app/store';
import { defaultValues, tuneCoreExportFormErrorMessages } from './constants';
import { ExportTuneCoreReleaseFormValues } from './types';

export const useExportTuneCoreFormValidations = ({
  releaseExportLink,
  genresDDL,
}: {
  releaseExportLink?: Link;
  genresDDL: DDL;
}) => {
  const [languageCodes] = useResource(
    fetchLanguageCodesDDLEffect,
    getLanguageCodesSelector
  );

  const {
    validationSchema,
    validationSchemaFetched,
  } = useFetchValidationSchema<TcReleaseExportValidations>({
    link: releaseExportLink,
  });

  const yupValidationSchema = useMemo(
    () =>
      validationSchemaFetched &&
      validationSchema &&
      transformSchemaToYup(
        validationSchema,
        defaultValues,
        tuneCoreExportFormErrorMessages
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validationSchema, validationSchemaFetched]
  );

  const concatenatedSchema = yup
    .object()
    .shape({
      releaseDate: yup
        .date()
        .nullable()
        .required(fieldValidationMessages.REQUIRED)
        .typeError('Invalid Date Format'),
      genre: yup.object().required(fieldValidationMessages.REQUIRED).nullable(),
      language: yup
        .object()
        .required(fieldValidationMessages.REQUIRED)
        .nullable(),
    })
    .concat(yupValidationSchema || yup.object());

  const { genres } = useGenres({
    genresDDL,
    validationSchemaFetched,
    genresEnum: validationSchema?.properties?.genre,
  });

  const languages = getOptions(
    validationSchema?.properties.language || { enum: [] },
    languageCodes || {}
  );

  return {
    validations: concatenatedSchema,
    validationsFetched: validationSchemaFetched,
    genres,
    languages,
  };
};

export const useFetchDefaultValues = ({
  release,
  genres: genreOptions,
  languages,
}: {
  release: TuneCoreRelease;
  genres: Option[];
  languages: Option[];
}) => {
  const dispatch = useAppDispatch();
  const [result, setResult] = useState<ExportTuneCoreReleaseFormValues>(
    defaultValues
  );
  const version = release.versions.length === 1 ? release.versions[0] : null;

  const fetchDefaultValues = async () => {
    const previousExport = release.latestExport?.tuneCore;

    if (!version && !previousExport) {
      return setResult(defaultValues);
    }

    if (previousExport) {
      return setResult({
        mainArtist: previousExport.mainArtist,
        recordingLocation: previousExport.recordingLocation,
        releaseDate: previousExport.releaseDate
          ? new Date(previousExport.releaseDate)
          : undefined,
        genre:
          genreOptions.find(({ value }) => value === previousExport.genre) ||
          null,
        language:
          languages.find(({ value }) => value === previousExport.language) ||
          null,
      });
    }

    try {
      const { song } = await dispatch(
        fetchReleaseVersionEffect({
          songGuid: version?.song.songGuid,
          versionGuid: version?.guid as string,
          releaseGuid: release.guid,
        })
      ).unwrap();
      const genres = song.overrides.genres || song.snapshot.genres || [];
      const language = song.overrides.language || song.snapshot.language;
      const defaultGenre =
        genreOptions.find(({ value }) => value === genres[0]) || null;
      const defaultLanguage =
        languages.find(({ value }) => value === language) || null;

      return setResult({
        ...defaultValues,
        genre: defaultGenre,
        language: defaultLanguage,
      });
    } catch (e) {
      return setResult(defaultValues);
    }
  };

  return {
    fetchDefaultValues,
    defaultValues: result,
  };
};
